import React from 'react';
import { motion } from 'framer-motion';
import {
  ArrowRight,
  Bell,
  Heart,
  LayoutDashboard,
  Search,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Tag,
} from 'lucide-react';
import { appMeta } from '@/config/iphonefastflip';

const previewItems = [
  { model: 'iPhone 15 Pro 256 GB', source: 'OLX', price: '3 899 zł', score: '+620 zł' },
  { model: 'iPhone 14 Pro 128 GB', source: 'Vinted', price: '2 749 zł', score: '+410 zł' },
  { model: 'iPhone 13 128 GB', source: 'Allegro', price: '1 749 zł', score: '+280 zł' },
];

const AppPreview: React.FC = () => (
  <div className="relative mx-auto w-full max-w-[650px] overflow-hidden rounded-[28px] border border-white/10 bg-[#090c12] shadow-[0_35px_100px_rgba(0,107,232,0.22)]">
    <div className="flex items-center justify-between border-b border-white/10 bg-[#0c1017] px-4 py-3">
      <div className="flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-gradient-to-br from-blue-500 to-cyan-400">
          <Smartphone className="h-4 w-4 text-white" />
        </div>
        <div>
          <p className="text-xs font-bold text-white">iPhoneFastFlip</p>
          <p className="text-[9px] text-slate-500">Przegląd rynku</p>
        </div>
      </div>
      <Bell className="h-4 w-4 text-slate-400" />
    </div>

    <div className="grid min-h-[430px] grid-cols-[72px_1fr] sm:grid-cols-[150px_1fr]">
      <aside className="border-r border-white/10 bg-[#10141c] p-2 sm:p-3">
        <div className="space-y-2">
          {[
            [LayoutDashboard, 'Przegląd'],
            [Search, 'Ogłoszenia'],
            [Heart, 'Ulubione'],
            [Bell, 'Alerty'],
          ].map(([Icon, label], index) => {
            const NavIcon = Icon as React.ElementType;
            return (
              <div
                key={label as string}
                className={`flex items-center gap-2 rounded-lg px-2 py-2.5 text-[10px] ${
                  index === 0 ? 'bg-blue-500/15 text-blue-300' : 'text-slate-500'
                }`}
              >
                <NavIcon className="h-4 w-4 shrink-0" />
                <span className="hidden sm:block">{label as string}</span>
              </div>
            );
          })}
        </div>
      </aside>

      <div className="overflow-hidden p-3 sm:p-5">
        <div className="mb-4 rounded-2xl border border-blue-400/10 bg-gradient-to-r from-blue-500/10 to-transparent p-4">
          <div className="mb-3 flex items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-blue-400">Rynek w jednym miejscu</p>
              <p className="mt-1 text-lg font-bold text-white sm:text-xl">Dzień dobry, łowco okazji</p>
            </div>
            <Sparkles className="h-6 w-6 text-blue-400" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              ['1 248', 'ofert'],
              ['37', 'okazji'],
              ['12', 'ulubionych'],
            ].map(([value, label]) => (
              <div key={label} className="rounded-xl bg-white/[0.035] px-2 py-2">
                <p className="text-sm font-bold text-white">{value}</p>
                <p className="text-[9px] text-slate-500">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="mb-3 flex items-center justify-between">
          <p className="text-xs font-semibold text-white">Najlepiej dopasowane</p>
          <span className="text-[10px] text-blue-400">Zobacz wszystkie</span>
        </div>
        <div className="space-y-2.5">
          {previewItems.map((item, index) => (
            <div key={item.model} className="flex items-center gap-3 rounded-xl border border-white/[0.07] bg-white/[0.025] p-2.5">
              <div className="grid h-12 w-12 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-slate-800 to-slate-900">
                <Smartphone className="h-6 w-6 text-slate-500" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="mb-1 flex items-center gap-2">
                  <span className="rounded bg-blue-500/15 px-1.5 py-0.5 text-[8px] font-bold text-blue-300">{item.source}</span>
                  {index === 0 && <span className="text-[8px] text-emerald-400">NOWA</span>}
                </div>
                <p className="truncate text-[11px] font-semibold text-white sm:text-xs">{item.model}</p>
                <p className="mt-0.5 text-[9px] text-slate-500">{item.price}</p>
              </div>
              <div className="rounded-lg bg-emerald-500/10 px-2 py-1 text-[9px] font-bold text-emerald-400">{item.score}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Hero: React.FC = () => (
  <section className="relative overflow-hidden px-4 pb-20 pt-28 sm:pt-32 lg:min-h-screen lg:pb-24">
    <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[0.9fr_1.1fr]">
      <div className="relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 inline-flex items-center gap-2 rounded-full border border-blue-400/20 bg-blue-500/10 px-3 py-1.5 text-sm font-semibold text-blue-300"
        >
          <span className="h-2 w-2 rounded-full bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)]" />
          Wersja webowa 1.0.0 jest gotowa do testów
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.08 }}
          className="max-w-3xl text-5xl font-black leading-[0.98] tracking-[-0.055em] text-white sm:text-6xl lg:text-7xl"
        >
          Znajduj iPhone’y, które{' '}
          <span className="gradient-text">warto odsprzedać.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.16 }}
          className="mt-7 max-w-2xl text-lg leading-8 text-slate-400 sm:text-xl"
        >
          {appMeta.name} łączy ogłoszenia z OLX, Allegro Lokalnie i Vinted, porównuje je z Twoim cennikiem i pomaga szybko wyłapać oferty z potencjałem.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.24 }}
          className="mt-9 flex flex-col gap-3 sm:flex-row"
        >
          <a href={appMeta.webUrl} className="button-primary group">
            Uruchom aplikację webową
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </a>
          <a href="#funkcje" className="button-secondary">
            Poznaj wszystkie funkcje
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.34 }}
          className="mt-8 grid max-w-xl grid-cols-1 gap-3 text-sm text-slate-400 sm:grid-cols-3"
        >
          <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-blue-400" /> Wspólne konto</span>
          <span className="flex items-center gap-2"><Tag className="h-4 w-4 text-blue-400" /> Własne cenniki</span>
          <span className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-blue-400" /> Android • Web • Windows</span>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, scale: 0.94, y: 28 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.14 }}
        className="relative z-10"
      >
        <div className="absolute -inset-10 -z-10 rounded-full bg-blue-500/10 blur-3xl" />
        <AppPreview />
      </motion.div>
    </div>
  </section>
);

export default Hero;
